/**
 * 1stbuddha — recently-viewed.js
 *
 * <recently-viewed> (sections/recently-viewed.liquid)
 *   - reads the handles assets/product.js stored in localStorage
 *     (`recently-viewed`, newest first), leaves out data-exclude (the product
 *     on screen), takes data-limit
 *   - once the band nears the viewport, renders each piece through
 *     /products/<handle>?section_id=product-card-fragment and shows the band;
 *     pieces that no longer exist are dropped from storage
 *   - syncs the wishlist hearts on the new cards
 */
(() => {
  if (customElements.get('recently-viewed')) return;

  const KEY = 'recently-viewed';
  const read = () => {
    try {
      const list = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list.filter((item) => typeof item === 'string') : [];
    } catch {
      return [];
    }
  };
  const forget = (handles) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(read().filter((item) => !handles.includes(item))));
    } catch {
      /* storage unavailable */
    }
  };

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      const exclude = this.dataset.exclude;
      const limit = Number(this.dataset.limit) || 4;
      this.handles = read()
        .filter((handle) => handle !== exclude)
        .slice(0, limit);
      if (!this.handles.length) return;

      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      // The band is hidden (no box), so watch its parent section instead.
      this.observer.observe(this.parentElement || this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async load() {
      const list = this.querySelector('[data-recently-viewed-list]');
      if (!list) return;
      const root = (this.dataset.root || '/').replace(/\/$/, '');
      const gone = [];

      const items = await Promise.all(
        this.handles.map(async (handle) => {
          try {
            const response = await fetch(`${root}/products/${encodeURIComponent(handle)}?section_id=product-card-fragment`);
            if (response.status === 404) gone.push(handle);
            if (!response.ok) return null;
            const html = await response.text();
            return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-card-fragment]');
          } catch {
            return null;
          }
        })
      );

      if (gone.length) forget(gone);
      const cards = items.filter(Boolean);
      if (!cards.length) return;
      list.replaceChildren(...cards);
      this.hidden = false;
      window.themeWishlist?.sync(this);
    }
  }

  customElements.define('recently-viewed', RecentlyViewed);
})();
